import { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
} from "@mui/material";
import AppButton from "../button/AppButton";
import AppDateInput from "../inputs/AppDateInput";
import AppTextInput from "../inputs/AppTextInput";
import { CreateRecurringExpenseRequest } from "../../models/RequestModels/CreateRecurringExpenseRequest";
import { PropertyResponse } from "../../models/ResponseModels/PropertyResponse";
import { RecurringExpenseResponse } from "../../models/ResponseModels/RecurringExpenseResponse";
import { UnitResponse } from "../../models/ResponseModels/UnitResponse";
import { useTranslation } from "../../translations/i18n";

interface RecurringExpenseFormModalProps {
  open: boolean;
  loading: boolean;
  properties: PropertyResponse[];
  units: UnitResponse[];
  recurringExpense?: RecurringExpenseResponse | null;
  defaultCurrency: string;
  onClose: () => void;
  onSubmit: (body: CreateRecurringExpenseRequest) => void;
}

const today = () => new Date().toISOString().split("T")[0];

const emptyToNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const RecurringExpenseFormModal = ({
  open,
  loading,
  properties,
  units,
  recurringExpense,
  defaultCurrency,
  onClose,
  onSubmit,
}: RecurringExpenseFormModalProps) => {
  const { t } = useTranslation();

  const [propertyId, setPropertyId] = useState<number | "">("");
  const [unitId, setUnitId] = useState<number | "">("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState(defaultCurrency);
  const [frequency, setFrequency] =
    useState<CreateRecurringExpenseRequest["frequency"]>("monthly");
  const [interval, setInterval] = useState("1");
  const [dayOfMonth, setDayOfMonth] = useState("1");
  const [startDate, setStartDate] = useState(today());
  const [endDate, setEndDate] = useState("");
  const [active, setActive] = useState(true);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }

    setPropertyId(recurringExpense?.property_id || "");
    setUnitId(recurringExpense?.unit_id || "");
    setName(recurringExpense?.name || "");
    setCategory(recurringExpense?.category || "");
    setAmount(
      recurringExpense?.amount ? String(Number(recurringExpense.amount)) : "",
    );
    setCurrency(recurringExpense?.currency || defaultCurrency);
    setFrequency(recurringExpense?.frequency || "monthly");
    setInterval(
      recurringExpense?.interval ? String(recurringExpense.interval) : "1",
    );
    setDayOfMonth(
      recurringExpense?.day_of_month
        ? String(recurringExpense.day_of_month)
        : "1",
    );
    setStartDate(recurringExpense?.start_date?.split("T")[0] || today());
    setEndDate(recurringExpense?.end_date?.split("T")[0] || "");
    setActive(recurringExpense?.active ?? true);
    setNotes(recurringExpense?.notes || "");
  }, [open, recurringExpense, defaultCurrency]);

  const filteredUnits = propertyId
    ? units.filter((unit) => unit.property_id === propertyId)
    : units;

  const handlePropertyChange = (value: number | "") => {
    setPropertyId(value);

    if (unitId) {
      const selectedUnit = units.find((unit) => unit.id === unitId);

      if (selectedUnit && value && selectedUnit.property_id !== value) {
        setUnitId("");
      }
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const handleSubmit = () => {
    if (!name.trim() || !amount || Number(amount) <= 0 || !startDate) {
      return;
    }

    onSubmit({
      property_id: propertyId || null,
      unit_id: unitId || null,
      name: name.trim(),
      category: emptyToNull(category),
      amount: Number(amount),
      currency,
      frequency,
      interval: Number(interval || 1),
      day_of_month: frequency === "monthly" ? Number(dayOfMonth || 1) : null,
      start_date: startDate,
      end_date: endDate || null,
      active,
      notes: emptyToNull(notes),
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 900 }}>
        {recurringExpense
          ? t("expenses.editRecurringExpense")
          : t("expenses.newRecurringExpense")}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <AppTextInput
            label={t("expenses.name")}
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={loading}
            required
            autoFocus
          />

          <AppTextInput
            label={t("expenses.category")}
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            placeholder={t("expenses.categoryPlaceholder")}
            disabled={loading}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <AppTextInput
              label={t("expenses.amount")}
              value={amount}
              type="number"
              onChange={(event) => setAmount(event.target.value)}
              disabled={loading}
              required
            />

            <AppTextInput
              label={t("booking.currency")}
              value={currency}
              onChange={(event) =>
                setCurrency(event.target.value.toUpperCase())
              }
              disabled={loading}
              required
            />
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>{t("expenses.frequency")}</InputLabel>
              <Select
                label={t("expenses.frequency")}
                value={frequency}
                disabled={loading}
                onChange={(event) =>
                  setFrequency(
                    event.target
                      .value as CreateRecurringExpenseRequest["frequency"],
                  )
                }
              >
                <MenuItem value="monthly">{t("expenses.monthly")}</MenuItem>
                <MenuItem value="yearly">{t("expenses.yearly")}</MenuItem>
              </Select>
            </FormControl>

            <AppTextInput
              label={t("expenses.interval")}
              value={interval}
              type="number"
              onChange={(event) => setInterval(event.target.value)}
              disabled={loading}
            />
          </Stack>

          {frequency === "monthly" ? (
            <AppTextInput
              label={t("expenses.dayOfMonth")}
              value={dayOfMonth}
              type="number"
              onChange={(event) => setDayOfMonth(event.target.value)}
              disabled={loading}
            />
          ) : null}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <AppDateInput
              label={t("expenses.startDate")}
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              disabled={loading}
              required
            />

            <AppDateInput
              label={t("expenses.endDate")}
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              disabled={loading}
            />
          </Stack>

          <FormControl fullWidth>
            <InputLabel>{t("properties.title")}</InputLabel>
            <Select
              label={t("properties.title")}
              value={propertyId}
              disabled={loading}
              onChange={(event) =>
                handlePropertyChange(event.target.value as number | "")
              }
            >
              <MenuItem value="">{t("expenses.noProperty")}</MenuItem>
              {properties.map((property) => (
                <MenuItem key={property.id} value={property.id}>
                  {property.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>{t("booking.unit")}</InputLabel>
            <Select
              label={t("booking.unit")}
              value={unitId}
              disabled={loading}
              onChange={(event) => setUnitId(event.target.value as number | "")}
            >
              <MenuItem value="">{t("expenses.noUnit")}</MenuItem>
              {filteredUnits.map((unit) => (
                <MenuItem key={unit.id} value={unit.id}>
                  {unit.property?.name ? `${unit.property.name} · ` : ""}
                  {unit.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={active}
                onChange={(event) => setActive(event.target.checked)}
                disabled={loading}
              />
            }
            label={t("expenses.active")}
          />

          <AppTextInput
            label={t("booking.notes")}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            disabled={loading}
            multiline
            minRows={3}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <AppButton
          title={t("common.cancel")}
          variant="outlined"
          color="inherit"
          fullWidth={false}
          disabled={loading}
          onClick={handleClose}
        />

        <AppButton
          title={recurringExpense ? t("common.update") : t("common.create")}
          fullWidth={false}
          loading={loading}
          disabled={!name.trim() || !amount || Number(amount) <= 0}
          onClick={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};

export default RecurringExpenseFormModal;
